// Invalidación selectiva de la caché del Service Worker para endpoints de
// API tras mutaciones. Sin esto, el SW (NetworkFirst con 24h de TTL en
// `api-v1`) puede devolver una respuesta cacheada vieja cuando la red falla
// — incluso si la IDB local ya está actualizada — y reintroducir en
// pantalla items que el usuario acaba de borrar o editar (fantasmas).
//
// Funciona enviando un postMessage al SW con la lista de URLs a borrar.
// El SW responde abriendo el cache `api-v1` y eliminando esas entradas
// con `ignoreSearch: true` para cubrir variantes con query string
// (?showStatusCargada=true|false, etc.).
//
// Si el SW no está activo (primera carga, navegador sin soporte) la
// llamada es no-op — la siguiente fetch online repoblará la caché con
// datos correctos de todos modos.

const SW_CACHE_NAME = 'api-v1';
const BASE_URL = __API_BASE_URL__;

interface InvalidateOpts {
  // Lista de details de una carga → invalida GetCargoDetailByIdCargo*.
  idCargo?: number;
  // Lista de imágenes de un detail → invalida GetImagesToAppController.
  idCargoDetail?: number;
  // Lista de positions de una fecha → invalida GetCargoByDateWithClaims.
  // Formato YYYY-MM-DD (o YYYY-M-D, normalizamos).
  dateKey?: string;
}

function buildUrls(opts: InvalidateOpts): string[] {
  const urls: string[] = [];
  if (opts.idCargo != null) {
    urls.push(`${BASE_URL}api/CargoDetail/GetCargoDetailByIdCargoAppControllerAsync/${opts.idCargo}`);
  }
  if (opts.idCargoDetail != null && opts.idCargoDetail > 0) {
    urls.push(`${BASE_URL}api/Blob/GetImagesToAppController/${opts.idCargoDetail}`);
  }
  if (opts.dateKey) {
    const parts = opts.dateKey.split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      urls.push(`${BASE_URL}api/Cargo/GetCargoByDateWithClaimsAsync/${Number(y)}/${Number(m)}/${Number(d)}`);
      // También los totales del mes (vista calendario) de ese día.
      urls.push(`${BASE_URL}api/Cargo/GetCargoMonthTotalsWithClaimsAsync/${Number(y)}/${Number(m)}`);
    }
  }
  return urls;
}

// Manda al SW activo la orden de borrar las URLs cacheadas. Best-effort:
// si no hay SW o el mensaje falla, no romper el flujo principal.
export function invalidateSwApiCache(opts: InvalidateOpts): void {
  const urls = buildUrls(opts);
  if (urls.length === 0) return;
  try {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    // Intentamos via controller (SW que está controlando la página). Si
    // aún no hay controller (primera carga sin recargar), via .ready
    // como fallback.
    const send = (target: ServiceWorker | null) => {
      if (!target) return false;
      target.postMessage({ type: 'INVALIDATE_CACHE', urls, cacheName: SW_CACHE_NAME });
      return true;
    };
    if (send(sw.controller)) return;
    sw.ready.then((reg) => send(reg.active)).catch(() => { /* sin op */ });
  } catch { /* sin op */ }
}
